import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AgendarCita from "../PatientComponent/AgendarCita";
import { alertaGeneral } from "../../utils/alertas";
import apiClient from "../../utils/axiosPetition";
import { useDataUser } from "../../context/dataUserContext";

const ID_TIPOS = [{ value: 'CC', label: 'Cédula de ciudadanía' }, { value: 'CE', label: 'Cédula de extranjería' }, { value: 'NIT', label: 'NIT' }, { value: 'PA', label: 'Pasaporte' }];

const getRespuestaEpayco = (r) =>
    r?.x_respuesta || r?.raw?.x_respuesta || r?.x_response || r?.raw?.x_response || '';

const isPagoAceptado = (r) => {
    const codigo = String(r?.x_cod_response ?? r?.raw?.x_cod_response ?? '');
    return getRespuestaEpayco(r) === 'Aceptada' || codigo === '1';
};

const isPagoRechazado = (r) => /rechaz|cancel|fallid/i.test(getRespuestaEpayco(r));

/** ePayco puede responder "Pendiente" al volver del checkout; reintentar hasta confirmar. */
const pollEstadoPagoCita = async (ref, attempts = 20, interval = 2500) => {
    for (let i = 0; i < attempts; i++) {
        try {
            const res = await apiClient.get(`/api/epayco/consultar-estado?ref_payco=${ref}`);
            const data = res.data;
            if (isPagoAceptado(data)) return { ok: true, data };
            if (isPagoRechazado(data)) return { ok: false, data };
        } catch (err) {
            console.warn('Polling estado ePayco:', err.response?.data || err.message);
        }
        await new Promise((resolve) => setTimeout(resolve, interval));
    }
    return { ok: null, data: null };
};

const PagRespuestaAgendarCitaEpayco = () => {
    const preInfo = JSON.parse(localStorage.getItem('selectedDate')) || {};
    const [searchParams] = useSearchParams();
    const refPayco = searchParams.get('ref_payco');
    const [estado, setEstado] = useState(null);
    const [isApproved, setIsApproved] = useState(false);
    const [showBuyerForm, setShowBuyerForm] = useState(false);
    const [showEmailFacturaForm, setShowEmailFacturaForm] = useState(false);
    const [emailFactura, setEmailFactura] = useState('');
    const [sendingEmailFactura, setSendingEmailFactura] = useState(false);
    const [facturaEnviada, setFacturaEnviada] = useState(false);
    const [buyerForm, setBuyerForm] = useState({ firstName: '', lastName: '', idType: 'CC', idNumber: '', email: '', cellNumber: '', address: '', city: '', department: '' });
    const [submittingFactura, setSubmittingFactura] = useState(false);
    const { patientId, role } = useDataUser();
    const navigate = useNavigate();

    const [dataCita, setDataCita] = useState({
        doctorId: preInfo.doctorId || '',
        doctor: preInfo.doctor || '',
        servicio: preInfo.servicio || '',
        precioServicio: preInfo.precioServicio || '',
        fecha: preInfo.fecha || '',
        tipoPagador: preInfo.tipoPagador || '',
        tipoConvenio: preInfo.tipoConvenio || 'sin convenio',
        motivoConsulta: preInfo.motivoConsulta || '',
        tipoCita: preInfo.tipoCita || '',
    });

    // Tras factura: la cita ya fue creada vía /completar-cita-pago; solo redirigir
    const onApproved = () => {
        const checkProfileComplete = async () => {
            try {
                if (role === 'doctor') {
                    const profileResponse = await apiClient.get('/api/patient/');
                    const patients = profileResponse.data;
                    if (patients?.length > 0 && !patients[0].profileComplete) {
                        alertaGeneral({
                            titulo: 'Pago y Cita Confirmados',
                            messageHtml: 'Tu pago fue exitoso y la cita quedó agendada. Completa tu perfil de paciente para acceder a la videoconsulta.',
                        }).then(() => navigate('/doctor/perfil'));
                        return;
                    }
                } else if (patientId) {
                    const profileResponse = await apiClient.get(`/api/patient/${patientId}`);
                    if (!profileResponse.data.profileComplete) {
                        alertaGeneral({
                            titulo: 'Pago y Cita Confirmados',
                            messageHtml: 'Tu pago fue exitoso y la cita quedó agendada. Completa tu perfil para acceder a la videoconsulta.',
                        }).then(() => navigate('/patient/perfil'));
                        return;
                    }
                }
                alertaGeneral({
                    titulo: 'Pago y Cita Confirmados',
                    messageHtml: 'Tu pago fue procesado con éxito y tu cita de teleconsulta quedó agendada. Revisa tu correo para firmar el consentimiento informado.',
                }).then(() => {
                    navigate(role === 'doctor' ? '/doctor/teleconsultas' : '/patient/teleconsultas', { state: { fromPayment: true } });
                });
            } catch (profileError) {
                console.error('Error verificando perfil:', profileError);
                navigate(role === 'doctor' ? '/doctor/teleconsultas' : '/patient/teleconsultas', { state: { fromPayment: true } });
            }
        };
        checkProfileComplete();
    };

    const completarCitaTrasPago = async () => {
        if (!refPayco) return false;
        try {
            const res = await apiClient.post('/api/epayco/completar-cita-pago', {
                ref_payco: refPayco,
                doctorId: dataCita.doctorId,
                patientId,
                nombrePaciente: preInfo.nombrePaciente || undefined,
                doctor: dataCita.doctor,
                motivoConsulta: dataCita.motivoConsulta,
                fecha: dataCita.fecha,
                tipoPagador: dataCita.tipoPagador,
                tipoConvenio: dataCita.tipoConvenio,
                tipoCita: dataCita.tipoCita,
                servicio: dataCita.servicio,
            });
            console.log('✅ Cita completada tras pago:', res.data);
            return true;
        } catch (err) {
            console.error('Error completando cita tras pago:', err);
            const conflict = err.response?.data?.code === 'APPOINTMENT_SLOT_CONFLICT' || err.response?.status === 409;
            const msg = err.response?.data?.message || 'No se pudo confirmar la cita automáticamente.';
            alertaGeneral({
                titulo: conflict ? 'Horario ya ocupado' : 'Pago recibido',
                messageHtml: conflict
                    ? `${msg} Tu pago fue procesado; contacta a soporte con la referencia ePayco <strong>${refPayco}</strong> para reprogramar o reembolso.`
                    : `${msg} Guarde la referencia ePayco <strong>${refPayco}</strong> y contacta a soporte si no recibe confirmación.`,
                isError: true,
            });
            return false;
        }
    };

    useEffect(() => {
        return () => {
            if (facturaEnviada || isApproved) {
                localStorage.removeItem("selectedDate");
            }
        };
    }, [facturaEnviada, isApproved]);

    // Pre-llenar datos del comprador con el perfil del paciente
    useEffect(() => {
        if (!showBuyerForm || !patientId) return;
        apiClient.get(`/api/patient/${patientId}`)
            .then(res => {
                const p = res.data;
                if (p) {
                    setBuyerForm(prev => ({
                        ...prev,
                        firstName: (p.firstName || '').trim() || prev.firstName,
                        lastName: (p.firstLastName || p.lastName || '').trim() || prev.lastName,
                        idType: p.idType || 'CC',
                        idNumber: (p.idNumber || '').toString().trim() || prev.idNumber,
                        email: (p.email || '').trim() || prev.email,
                        cellNumber: (p.cellNumber || p.phone || '').toString().trim() || prev.cellNumber,
                        address: (p.address || '').trim() || prev.address,
                        city: (p.city || '').trim() || prev.city,
                        department: (p.department || '').trim() || prev.department,
                    }));
                }
            })
            .catch(() => {});
    }, [showBuyerForm, patientId]);

    const handleSubmitBuyerForm = async (e) => {
        e.preventDefault();
        const { firstName, lastName, idType, idNumber, email, cellNumber, address, city, department } = buyerForm;
        if (!firstName?.trim() || !lastName?.trim() || !idNumber?.trim() || !email?.trim() || !city?.trim()) {
            alertaGeneral({ titulo: 'Datos incompletos', messageHtml: 'Completa nombre, apellido, documento, correo y ciudad del comprador para la factura.', isError: true });
            return;
        }
        setSubmittingFactura(true);
        try {
            await apiClient.post('/api/epayco/datos-comprador-factura-cita', {
                referenceCode: refPayco,
                ref_payco: refPayco,
                doctorId: dataCita.doctorId,
                patientId,
                motivoConsulta: dataCita.motivoConsulta,
                fecha: dataCita.fecha,
                tipoPagador: dataCita.tipoPagador,
                tipoConvenio: dataCita.tipoConvenio,
                tipoCita: dataCita.tipoCita,
                servicio: dataCita.servicio,
                doctor: dataCita.doctor,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                idType: idType || 'CC',
                idNumber: (idNumber || '').trim().replace(/\D/g, '').slice(0, 15) || idNumber?.trim(),
                email: (email || '').trim(),
                cellNumber: (cellNumber || '').trim(),
                address: (address || '').trim() || undefined,
                city: (city || '').trim() || undefined,
                department: (department || '').trim() || undefined
            });
            setEmailFactura((email || '').trim());
            setShowBuyerForm(false);
            setShowEmailFacturaForm(true);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error al generar la factura';
            alertaGeneral({ titulo: 'Error al generar factura', messageHtml: msg, isError: true });
        } finally {
            setSubmittingFactura(false);
        }
    };

    const handleSubmitEmailFactura = async (e) => {
        e.preventDefault();
        const emailTrim = (emailFactura || '').trim();
        if (!emailTrim) {
            alertaGeneral({ titulo: 'Correo requerido', messageHtml: 'Indique el correo al que desea recibir la factura.', isError: true });
            return;
        }
        setSendingEmailFactura(true);
        try {
            await apiClient.post('/api/epayco/enviar-factura-cita-email', {
                referenceCode: refPayco,
                ref_payco: refPayco,
                email: emailTrim
            });
            alertaGeneral({ titulo: 'Factura enviada', messageHtml: `Se ha enviado la factura al correo <strong>${emailTrim}</strong>. Revisa tu bandeja de entrada.` });
            setFacturaEnviada(true);
            onApproved();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'No se pudo enviar la factura por correo.';
            alertaGeneral({ titulo: 'Error', messageHtml: msg, isError: true });
        } finally {
            setSendingEmailFactura(false);
        }
    };

    const handleOmitirEmailFactura = () => {
        setFacturaEnviada(true);
        onApproved();
    };

    useEffect(() => {
        if (!refPayco || isApproved) return;

        let cancelled = false;

        const validarPago = async () => {
            const { ok, data } = await pollEstadoPagoCita(refPayco);
            if (cancelled) return;

            const respuesta = getRespuestaEpayco(data);

            if (ok === true) {
                const citaOk = await completarCitaTrasPago();
                if (cancelled) return;

                if (citaOk) {
                    setEstado('Aceptada');
                    alertaGeneral({
                        titulo: 'Transacción Aprobada',
                        messageHtml: 'La transacción fue aprobada y tu cita quedó agendada. Indique los datos del comprador para generar la factura.',
                    }).then(() => {
                        setShowBuyerForm(true);
                        setIsApproved(true);
                    });
                    return;
                }
            }

            if (ok === null) {
                const citaOk = await completarCitaTrasPago();
                if (cancelled) return;
                if (citaOk) {
                    setEstado('Aceptada');
                    alertaGeneral({
                        titulo: 'Transacción Aprobada',
                        messageHtml: 'La transacción fue aprobada y tu cita quedó agendada. Indique los datos del comprador para generar la factura.',
                    }).then(() => {
                        setShowBuyerForm(true);
                        setIsApproved(true);
                    });
                    return;
                }
            }

            setEstado(respuesta || 'Rechazada');

            if (isPagoRechazado(data)) {
                alertaGeneral({
                    titulo: 'Pago no completado',
                    messageHtml: respuesta.toLowerCase().includes('cancel')
                        ? 'No se realizó el cobro. Puedes intentar agendar de nuevo cuando quieras.'
                        : `La transacción no fue aprobada. Estado: ${respuesta}`,
                    isError: true,
                });
                return;
            }

            alertaGeneral({
                titulo: 'Pago en proceso',
                messageHtml: `El pago aún se está confirmando (estado: ${respuesta || 'desconocido'}). Si ya se realizó el cobro, revisa tu correo o Mis citas en unos minutos. Referencia: <strong>${refPayco}</strong>`,
                isError: false,
            });
        };

        validarPago();

        return () => {
            cancelled = true;
        };
    }, [refPayco, isApproved]);

    if (!estado) {
        return <div className="flex flex-col items-center justify-center min-h-[200px]">Consultando estado del pago...</div>;
    }
    if (estado !== "Aceptada") {
        return <AgendarCita />;
    }

    if (facturaEnviada) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-[#1f94c2]/25 border-t-[#1f94c2] animate-spin" />
                <p className="text-slate-700 font-medium">Completando su reserva y redirigiendo...</p>
            </div>
        );
    }

    if (showEmailFacturaForm) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-6">
                <div className="w-full max-w-md p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800 mb-1">Recibir factura por correo</h2>
                    <p className="text-sm text-gray-600 mb-4">Indique el correo al que desea que le enviemos la factura electrónica de Siigo.</p>
                    <form onSubmit={handleSubmitEmailFactura} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico *</label>
                            <input
                                type="email"
                                value={emailFactura}
                                onChange={e => setEmailFactura(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2]"
                                placeholder="correo@ejemplo.com"
                                required
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                type="submit"
                                disabled={sendingEmailFactura}
                                className="flex-1 py-2.5 px-4 rounded-lg font-medium text-white bg-[#1f94c2] hover:bg-[#187a9e] disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {sendingEmailFactura ? 'Enviando...' : 'Enviar factura y continuar'}
                            </button>
                            <button
                                type="button"
                                onClick={handleOmitirEmailFactura}
                                disabled={sendingEmailFactura}
                                className="flex-1 py-2.5 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 disabled:opacity-60"
                            >
                                Continuar sin enviar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    if (showBuyerForm) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-6">
                <div className="w-full max-w-lg p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800 mb-1">Pago exitoso</h2>
                    <p className="text-sm text-gray-600 mb-4">Indique los datos del comprador para generar la factura (pueden ser distintos a los del paciente).</p>
                    <form onSubmit={handleSubmitBuyerForm} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                <input type="text" value={buyerForm.firstName} onChange={e => setBuyerForm(f => ({ ...f, firstName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Nombre" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                                <input type="text" value={buyerForm.lastName} onChange={e => setBuyerForm(f => ({ ...f, lastName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Apellido" required />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento</label>
                            <select value={buyerForm.idType} onChange={e => setBuyerForm(f => ({ ...f, idType: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                {ID_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Número de documento *</label>
                            <input type="text" value={buyerForm.idNumber} onChange={e => setBuyerForm(f => ({ ...f, idNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Documento" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Correo del comprador *</label>
                            <input type="email" value={buyerForm.email} onChange={e => setBuyerForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="correo@ejemplo.com" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Celular</label>
                            <input type="text" value={buyerForm.cellNumber} onChange={e => setBuyerForm(f => ({ ...f, cellNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Celular" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                            <input type="text" value={buyerForm.address} onChange={e => setBuyerForm(f => ({ ...f, address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Calle, número, barrio (opcional)" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                                <input type="text" value={buyerForm.city} onChange={e => setBuyerForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Bogotá, Medellín" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                                <input type="text" value={buyerForm.department} onChange={e => setBuyerForm(f => ({ ...f, department: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Cundinamarca, Antioquia" />
                            </div>
                        </div>
                        <button type="submit" disabled={submittingFactura} className="w-full mt-2 py-2.5 px-4 rounded-lg font-medium text-white bg-[#1f94c2] hover:bg-[#187a9e] disabled:opacity-60 disabled:cursor-not-allowed">
                            {submittingFactura ? 'Generando factura...' : 'Generar factura y continuar'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return null;
};

export default PagRespuestaAgendarCitaEpayco; 